import { useUser } from "@clerk/react";
import { useCallback, useEffect, useRef } from "react";
import { AnalyticsApi } from "../api/analytics-api";
import { useMatch } from "react-router";
import { useSelfQuery } from "../queries/user-queries";

const INACTIVITY_THRESHOLD = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
const ACTIVITY_EVENTS = ["mousedown", "keypress", "touchstart", "click"];

// localStorage keys for tracking previous user
const USER_ID_STORAGE_KEY = "user_id";

export default function UserSessionTracker() {
  const { isSignedIn, isLoaded, user } = useUser();

  // We need to ensure that useSelfQuery is executed at least once because
  // there is a special handling of the user in the Clerk sign-in flow in backend.
  // Related: https://linear.app/amplifiles/issue/AMP-128/fix-clerk-to-amplifiles-user-sync
  const { isSuccess: isSelfSuccess } = useSelfQuery();

  // eslint-disable-next-line react-hooks/purity
  const lastActivityTime = useRef<number>(Date.now());

  const match = useMatch("/teams/:teamExternalId/*");
  const teamExternalId = match?.params.teamExternalId;

  const updateActivity = useCallback(() => {
    lastActivityTime.current = Date.now();
  }, []);

  // Handle login detection (as soon as Clerk is ready)
  useEffect(() => {
    if (!isLoaded || !isSelfSuccess) return;

    const currentUserId = user?.id || "";
    const lastUserId = localStorage.getItem(USER_ID_STORAGE_KEY);
    localStorage.setItem(USER_ID_STORAGE_KEY, currentUserId);
    if (!isSignedIn || currentUserId === "") return;

    // First time login detection
    if (!lastUserId || lastUserId === "") {
      AnalyticsApi.logUserLogin();
      updateActivity();
    }
  }, [isSignedIn, isLoaded, user?.id, isSelfSuccess, updateActivity]);

  // Handle session start logging (wait for team data to be ready)
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !isSelfSuccess) return;

    const currentUserId = user?.id || "";
    if (currentUserId === "") return;

    AnalyticsApi.logUserSessionStart(teamExternalId);
    updateActivity();
  }, [
    isSignedIn,
    isLoaded,
    user?.id,
    isSelfSuccess,
    updateActivity,
    teamExternalId,
  ]);

  // Set up activity listeners (only runs when user signs in/out)
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !isSelfSuccess) return;

    const handleActivity = () => {
      const timeSinceLastActivity = Date.now() - lastActivityTime.current;
      const wasInactive = timeSinceLastActivity >= INACTIVITY_THRESHOLD;

      updateActivity();

      // If user was inactive for 2+ hours and is now active, log new session
      if (wasInactive) {
        AnalyticsApi.logUserSessionStart(teamExternalId);
      }
    };

    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [isSignedIn, isLoaded, isSelfSuccess, updateActivity, teamExternalId]);

  return null;
}
